<template>
  <div class="episode-player">
    <div class="episode-player__inner">
      <button
        v-if="isEpisodePast"
        class="episode-player__play-button"
        @click="stopPlayer(), toggleEpisodePlayer()"
      >
        <Icon
          v-if="!isPlaying"
          name="PlayOutline"
        />
        <Icon
          v-if="isPlaying"
          name="PlusOutline"
          class="episode-player__player-icon episode-player__player-icon--cancel"
        />
      </button>
      <div class="episode-player__desc">
        <div class="episode-player__recording">
          <div class="episode-player__date">
            {{ useFormatDate('fullDate', start) }}
          </div>
          <div class="episode-player__duration">
            {{ duration }}
          </div>
          <NuxtLink
            v-if="show"
            :to="show.path.alias"
            class="episode-player__show-link"
          >
            Sendungsformat
          </NuxtLink>
        </div>
        <div class="episode-player__meta">
          <div
            v-if="icons.length || repeat"
            class="episode-player__categories-list"
          >
            <div
              v-for="(icon) in icons"
              :key="`episode-player__category--${icon.uuid}`"
              class="episode-player__category"
            >
              <Icon :name="`${icon.label}`" />
            </div>
            <div
              v-if="repeat"
              class="episode-player__category"
            >
              <Icon name="Repetition" />
            </div>
          </div>
          <div
            v-if="languages.length"
            class="episode-player__languages-list"
          >
            <div
              v-for="(language) in languages"
              :key="`episode-player__language--${language.uuid}`"
              class="episode-player__language"
            >
              {{ language.label }}
            </div>
          </div>
          <div
            v-show="isPlaying"
            class="episode-player__player"
          >
            <audio
              ref="audio"
              controls
              class="episode-player__audio"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { isPast } from 'date-fns'
import Hls from 'hls.js'

const { stopPlayer } = usePlayer()

const audio: Ref<HTMLAudioElement | null> = ref(null)

const props = defineProps<{
  tags: {
    label: string
  }
  languages: [
    {
      uuid: string
      label: string
    },
  ]
  show: {
    uuid: string
    url: {
      path: string
    }
  }
  start: string
  end: string
  repeat: boolean
}>()

const icons = computed(() => {
  return props.tags.map((tag) => {
    return {
      label: useCategoryIcon(tag.label),
      uuid: tag.uuid,
    }
  }).filter((icon) => {
    return icon.label !== undefined
  })
})

const src = computed(() => {
  return `https://archiv.loratech.ch/sendungen/${useFormatDate('archiveDate', props.start)}/${useFormatDate('archiveTime', props.start)}.m3u8`
})

const duration = computed(() => {
  return useFormatDuration(props.start, props.end)
})

const isEpisodePast = isPast(`${props.end}Z`)

const isPlaying = ref(false)

async function toggleEpisodePlayer() {
  if (isPlaying.value) {
    audio.value.pause()
  }
  else {
    await preparePlayer()
    audio.value.play()
  }
  isPlaying.value = !isPlaying.value
}

async function preparePlayer() {
  const stream = src.value
  if (audio.value) {
    if (Hls.isSupported()) {
      const hls = new Hls()
      hls.loadSource(stream)
      hls.attachMedia(audio.value)
    }
    else if (audio.value.canPlayType('application/vnd.apple.mpegurl')) {
      audio.value.src = stream
    }
  }
}
</script>

<style lang="scss">
@use "~/assets/sass/tools";

.episode-player {
  &__inner {
    display: flex;
    align-items: flex-start;
    gap: calc(var(--space--m) / 2);
  }

  &__play-button {
    @include tools.button-borderless; // stylelint-disable-line
    min-width: 51px;
    padding: 0;

    &:hover {
      color: var(--color-text);
      background-color: var(--color-background);
    }
  }

  &__player-icon {
    &--cancel {
      display: block;
      transform: rotate(45deg);
    }
  }

  &__desc {
    display: flex;
    flex-direction: column;
    gap: var(--space--xs);
  }

  &__recording {
    display: grid;
    grid-template-columns: repeat(3, auto);
    gap: 0 var(--grid-gutter);
  }

  &__show-link {
    @include tools.to(tools.$breakpoint--desktop) {
      grid-row: 2;
      grid-column: span 2;
    }
  }

  &__meta {
    display: flex;
    align-items: flex-start;
    gap: var(--space--s);

    @include tools.to(tools.$breakpoint--desktop) {
      flex-direction: column;
    }
  }

  &__categories-list,
  &__languages-list {
    display: flex;
    gap: var(--space--s);
    flex-wrap: wrap;
  }

  &__language {
    @include tools.tag;
  }

  &__player,
  &__audio {
    width: 100%;
  }

  &__audio {

    //&::-webkit-media-controls-mute-button,
    &::-webkit-media-controls-play-button,
    //&::-webkit-media-controls-volume-slider-container,
    //&::-webkit-media-controls-volume-slider,
    &::-webkit-media-controls-seek-back-button,
    &::-webkit-media-controls-seek-forward-button,
    &::-webkit-media-controls-fullscreen-button,
    &::-webkit-media-controls-rewind-button,
    &::-webkit-media-controls-return-to-realtime-button,
    &::-webkit-media-controls-toggle-closed-captions-button {
      display: none;
    }
  }
}
</style>
